import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  Filter,
  ChevronDown,
  FileSpreadsheet,
  XCircle,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getPreApprovalReport } from '../../services/reports';
import { getPartnersList } from '../../services/merchant';
import ReportDateRangeEnforcer from '../../components/ReportDateRangeEnforcer';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';

// Types
interface PreApproval {
  id: string;
  orderRef: string;
  customerName: string;
  merchantName: string;
  brandName: string;
  lender: string;
  amount: number;
  status: 'approved' | 'pending' | 'rejected';
  createdAt: string;
  approvedAt?: string;
}

interface ReportFilters {
  dateRange: {
    from: string | null;
    to: string | null;
  };
  brand: string;
  merchant: string;
  lender: string;
}

interface SelectOption {
  value: string;
  label: string;
}
let cachedBrandOptions: SelectOption[] | null = null;
let cachedLenderOptions: SelectOption[] | null = null;

const PreApprovalReportPage: React.FC = () => {
  const [data] = useState<PreApproval[]>([]);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: {
      from: null,
      to: null
    },
    brand: '',
    merchant: '',
    lender: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat] = useState<'excel'>('excel');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [lenderOptions, setLenderOptions] = useState<SelectOption[]>([]);
  const [isExportingToday, setIsExportingToday] = useState(false);

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const { user } = useAppSelector(state => state.auth);

  useEffect(() => {
    if (cachedBrandOptions && cachedLenderOptions) {
      setBrandOptions(cachedBrandOptions);
      setLenderOptions(cachedLenderOptions);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const categories = Array.isArray(user?.groups) ? user.groups : [];

        // 🔥 Backend filters partners by category
        const partners = await getPartnersList(categories);

        const brandOpts = partners.map(name => ({
          value: name,
          label: name,
        }));

        const lenderOpts = ['cashe', 'zype', 'cashe-ev'].map(name => ({
          value: name,
          label: name,
        }));

        // ✅ cache results
        cachedBrandOptions = brandOpts;
        cachedLenderOptions = lenderOpts;

        setBrandOptions(brandOpts);
        setLenderOptions(lenderOpts);
      } catch (error) {
        console.error('Error fetching options:', error);
        setBrandOptions([]);
        setLenderOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);


  useEffect(() => {
    // Check if filter is applied
    const hasDateRange = filters.dateRange.from && filters.dateRange.to;
    const hasOtherFilters = filters.brand || filters.merchant || filters.lender;

    setIsFilterApplied(!!hasDateRange && !!hasOtherFilters);
  }, [filters]);

  const handleExportData = async (format: 'excel') => {
    if (format !== 'excel') return;

    setIsExporting(true);
    setError(null); // Clear previous errors

    try {
      if (filters.dateRange.from && filters.dateRange.to) {
        const fromDate = new Date(filters.dateRange.from);
        const toDate = new Date(filters.dateRange.to);
        if (fromDate > toDate) {
          setError('From date cannot be later than To date');
          return;
        }
      }

      const params = {
        partner: filters.brand || undefined,
        merchant: filters.merchant || undefined,
        lender: filters.lender || undefined,
        from: filters.dateRange.from || undefined,
        to: filters.dateRange.to || undefined
      };

      // CleverPe User validation
      // if (user?.role === UserRole.CLEVERPE_USER) {
      //   const hasDateRange = filters.dateRange.from && filters.dateRange.to;
      //   const hasOtherFilters = filters.brand || filters.merchant || filters.lender;

      //   if (!hasDateRange) {
      //     setError('Date range is required for CleverPe users');
      //     return;
      //   }

      //   if (!hasOtherFilters) {
      //     setError('At least one additional filter is required for CleverPe users');
      //     return;
      //   }
      // } else if (!params.partner && !params.merchant && !params.lender && !params.from && !params.to) {
      //   setError('Please select at least one filter');
      //   return;
      // }

      const blob = await getPreApprovalReport(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preapprovals-${new Date().toISOString()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('Error exporting data:', error);
      setError(error?.message || 'Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportTodayData = async () => {
    if (
      !(
        filters?.lender ||
        filters?.merchant ||
        filters?.brand
      )
    ) {
      setError('Please select at least one filter');
      return;
    }

    setIsExportingToday(true);
    setError(null);

    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD

      // Build the export params with today's date range
      const params: any = {
        from: todayStr,
        to: todayStr
      };

      // Conditionally add filters if present
      if (filters.lender) params.lender = filters.lender;
      if (filters.merchant) params.merchant = filters.merchant;
      if (filters.brand) params.partner = filters.brand;

      const blob = await getPreApprovalReport(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preapprovals-today-${todayStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error exporting today data:', error);
      setError(error?.message || "Failed to export today's data. Please try again.");
    } finally {
      setIsExportingToday(false);
    }
  };

  return (
    <div className={`w-full space-y-6 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Error Message */}
      {error && (
        <div className="p-4 duration-300 border-l-4 border-red-400 rounded-md bg-red-50 animate-in fade-in slide-in-from-top">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-500"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Export button at top right */}
      {user?.role !== UserRole.CLEVERPE_USER && (
        <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <div></div>
          <div className="flex space-x-3">
            <Button
              isLoading={isExporting && exportFormat === 'excel'}
              onClick={() => handleExportData('excel')}
              className="flex items-center"
            >
              <FileSpreadsheet size={16} className="mr-1.5" />
              {isExporting && exportFormat === 'excel' ? 'Downloading...' : 'Export Excel'}
            </Button>
          </div>
        </div>
      )}

      {/* Filter panel - Always visible */}
      <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-800">Report Filters</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {/* Date range filters */}
          {user?.role !== UserRole.CLEVERPE_USER && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block mb-1 text-xs text-gray-500">From</label>
                  <input
                    type="date"
                    value={filters.dateRange.from || ''}
                    onChange={(e) =>
                      setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, from: e.target.value || null },
                      }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs text-gray-500">To</label>
                  <input
                    type="date"
                    value={filters.dateRange.to || ''}
                    onChange={(e) =>
                      setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, to: e.target.value || null },
                      }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
          {/* Brand filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Partner/Brand</label>
            <Select
              options={brandOptions}
              value={brandOptions.find(option => option.value === filters.brand)}
              onChange={(option) => setFilters(prev => ({
                ...prev,
                brand: option?.value || ''
              }))}
              isClearable
              placeholder="Select Brand"
            />
          </div>

          {/* Merchant filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Merchant</label>
            <input
              type="text"
              value={filters.merchant}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                merchant: e.target.value
              }))}
              placeholder="Enter merchant ID"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Lender filter */}
          {user?.role !== UserRole.CLEVERPE_USER && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Lender</label>
              <Select
                options={lenderOptions}
                value={lenderOptions.find(option => option.value === filters.lender)}
                onChange={(option) =>
                  setFilters(prev => ({
                    ...prev,
                    lender: option?.value || ''
                  }))
                }
                isClearable
                placeholder="Select Lender"
              />
            </div>
          )}
        </div>

        {/* Filter actions */}
        <div className="flex justify-end mt-6 space-x-3">
          {user?.role !== UserRole.CLEVERPE_USER && (
            <Button
              variant="outline"
              onClick={() => setFilters({
                dateRange: { from: null, to: null },
                brand: '',
                merchant: '',
                lender: ''
              })}
              size="sm"
            >
              Reset Filters
            </Button>
          )}
          <Button
            isLoading={isExportingToday}
            onClick={handleExportTodayData}
            size="sm"
            className="flex items-center"
            disabled={!(
              filters?.lender ||
              filters?.merchant ||
              filters?.brand
            )}
          >
            <FileSpreadsheet size={16} className="mr-1.5" />
            Today's Report
          </Button>
          {user?.role !== UserRole.CLEVERPE_USER && (
            <Button
              onClick={() => handleExportData('excel')}
              size="sm"
            >
              Apply Filters
            </Button>
          )}
        </div>
      </div>

      {/* Wrap the export section with ReportDateRangeEnforcer */}
      <ReportDateRangeEnforcer
        dateRange={{
          startDate: filters.dateRange.from ? new Date(filters.dateRange.from) : null,
          endDate: filters.dateRange.to ? new Date(filters.dateRange.to) : null
        }}
        isFilterApplied={isFilterApplied}
      >
        {user?.role !== UserRole.CLEVERPE_USER && (
          <div className="p-6 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <div className="mb-4 md:mb-0">
                <h3 className="text-lg text-gray-800 font-gilroy-bold">Export Merchant Data</h3>
                <p className="mt-1 text-gray-600">
                  {isExporting
                    ? 'Preparing your download...'
                    : error
                      ? 'Please check the filters and try again'
                      : 'Download the complete merchant database with all applied filters'}
                </p>
              </div>
              <div className="flex space-x-3">
                <Button
                  isLoading={isExporting}
                  onClick={() => handleExportData('excel')}
                  disabled={isLoading || Boolean(error)}
                  className="flex items-center"
                >
                  <FileSpreadsheet size={16} className="mr-1.5" />
                  Excel
                </Button>
              </div>
            </div>
          </div>
        )}
      </ReportDateRangeEnforcer>
    </div>
  );
};

export default PreApprovalReportPage;